import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Space, Tag, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { apiRequest, jsonBody } from '../../platform/api';

type AnnouncementCategory = 'release' | 'bugfix' | 'resource' | 'custom';

interface RuntimeAnnouncement {
  id: string;
  category: AnnouncementCategory;
  title: string;
  content: string;
  targetVersion?: string | null;
  revision: number;
  author?: string;
  publishedAt?: string | null;
}

interface RuntimeInfo {
  version: string;
  announcements: RuntimeAnnouncement[];
}

const categoryMeta: Record<AnnouncementCategory, { label: string; color: string }> = {
  release: { label: '版本更新', color: 'blue' },
  bugfix: { label: '问题修复', color: 'green' },
  resource: { label: '资源更新', color: 'purple' },
  custom: { label: '公告', color: 'gold' },
};

export function RuntimeNoticeCenter() {
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [announcements, setAnnouncements] = useState<RuntimeAnnouncement[]>([]);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateDeferred, setUpdateDeferred] = useState(false);
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);

  const versionMismatch = Boolean(serverVersion && serverVersion !== __APP_VERSION__);

  const checkRuntime = useCallback(async () => {
    if (!window.CLOUD_RUNTIME || document.visibilityState !== 'visible') return;
    try {
      const runtime = await apiRequest<RuntimeInfo>('/api/system/runtime');
      setServerVersion(runtime.version);
      if (runtime.version !== __APP_VERSION__) {
        setAnnouncementOpen(false);
        const noticeKey = `release-notice:${__APP_VERSION__}->${runtime.version}`;
        if (sessionStorage.getItem(noticeKey) !== 'deferred') {
          setUpdateModalOpen(true);
          setUpdateDeferred(false);
        } else {
          setUpdateDeferred(true);
        }
        return;
      }
      setUpdateModalOpen(false);
      setUpdateDeferred(false);
      setAnnouncements(runtime.announcements || []);
      setAnnouncementOpen((runtime.announcements || []).length > 0);
    } catch {
      // A deployment or temporary network interruption is not a version update.
    }
  }, []);

  useEffect(() => {
    void checkRuntime();
    const timer = window.setInterval(() => void checkRuntime(), 60_000);
    const handleVisible = () => {
      if (document.visibilityState === 'visible') void checkRuntime();
    };
    const handleOnline = () => void checkRuntime();
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('online', handleOnline);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('online', handleOnline);
    };
  }, [checkRuntime]);

  const deferUpdate = () => {
    if (serverVersion) sessionStorage.setItem(`release-notice:${__APP_VERSION__}->${serverVersion}`, 'deferred');
    setUpdateModalOpen(false);
    setUpdateDeferred(true);
  };

  const markAnnouncementsRead = async () => {
    if (markingRead) return;
    setMarkingRead(true);
    try {
      const receipts = await Promise.all(announcements.map((announcement) => apiRequest<{ read: boolean; stale?: boolean }>(
        `/api/system/announcements/${announcement.id}/read`,
        { method: 'POST', ...jsonBody({ revision: announcement.revision }) },
      )));
      if (receipts.some((receipt) => !receipt.read)) {
        message.info('公告已更新，正在加载最新内容');
        await checkRuntime();
        return;
      }
      setAnnouncementOpen(false);
      setAnnouncements([]);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '公告状态保存失败，请重试');
    } finally {
      setMarkingRead(false);
    }
  };

  const updateDescription = useMemo(() => (
    `当前版本 v${__APP_VERSION__}，服务器版本 v${serverVersion || '-'}。刷新后即可使用最新功能。`
  ), [serverVersion]);

  if (!window.CLOUD_RUNTIME) return null;

  return <>
    {versionMismatch && updateDeferred && (
      <Alert
        type="info"
        showIcon
        closable={false}
        className="runtime-update-banner"
        message="检测到版本更新"
        description={updateDescription}
        action={<Button type="primary" size="small" icon={<ReloadOutlined />} onClick={() => window.location.reload()}>立即刷新</Button>}
      />
    )}
    <Modal
      title="检测到版本更新"
      open={updateModalOpen}
      closable={false}
      maskClosable={false}
      okText="立即刷新"
      cancelText="稍后刷新"
      onOk={() => window.location.reload()}
      onCancel={deferUpdate}
    >
      <p>{updateDescription}</p>
      <p className="runtime-notice-muted">云端生成任务不会中断；尚未提交的选图和表单内容可能会在刷新后清空。</p>
    </Modal>
    <Modal
      title="最新公告"
      open={!versionMismatch && announcementOpen}
      closable={false}
      maskClosable={false}
      footer={<Button type="primary" loading={markingRead} onClick={() => void markAnnouncementsRead()}>我知道了</Button>}
    >
      <div className="announcement-list">
        {announcements.map((announcement) => {
          const meta = categoryMeta[announcement.category];
          return <article className="announcement-item" key={`${announcement.id}:${announcement.revision}`}>
            <Space wrap size={6}>
              <Tag color={meta.color}>{meta.label}</Tag>
              {announcement.targetVersion && <Tag>v{announcement.targetVersion}</Tag>}
            </Space>
            <h3>{announcement.title}</h3>
            <p>{announcement.content}</p>
            <small>{announcement.author ? `发布人：${announcement.author}` : ''}</small>
          </article>;
        })}
      </div>
    </Modal>
  </>;
}
